/**
 * Whether this tab is reloading for an app update its user accepted.
 *
 * Accepting an update activates the new service worker, and activation deletes
 * the old build's precache. A route chunk the old build requests in that window
 * fails because of the switch rather than because the tab is stale, and the
 * reload that stale-chunk recovery would start is already on its way.
 * `lazy-route.ts` reads this flag to stand down, so the page reloads once in
 * total.
 *
 * Module state rather than storage, because the flag describes this document
 * only and must not survive the reload it announces.
 */
let updateReloadPending = false;

/** Callbacks waiting for a pending update reload to end without a reload. */
const clearedListeners = new Set<() => void>();

/**
 * Marks the update reload as started, or clears it when an accepted update
 * ends without reloading (its worker was discarded before activating, or the
 * user cancelled the reload). Clearing runs every `onUpdateReloadCleared`
 * callback once.
 */
export function setUpdateReloadPending(pending: boolean): void {
  const wasPending = updateReloadPending;
  updateReloadPending = pending;
  if (wasPending && !pending) {
    const listeners = [...clearedListeners];
    clearedListeners.clear();
    for (const listener of listeners) {
      listener();
    }
  }
}

/**
 * Runs `listener` once when the pending update reload is cleared, which means
 * this document survived and nothing replaced it. A document that does reload
 * never runs it.
 */
export function onUpdateReloadCleared(listener: () => void): void {
  clearedListeners.add(listener);
}

export function isUpdateReloadPending(): boolean {
  return updateReloadPending;
}
